import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { Assert } from 'shared/assert';
import { useSelectedStorageAreaStore } from './selectedStorageAreaStore';
import type { Coordinate } from 'shared/map/domain/coordinate';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';
import { getMode, type ShapeGeoJson } from 'shared/shapes/domain/shapeGeoJson';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import type { ShapeResponse } from '@/shapes/api/models/shapeResponse';
import { useRefreshShapeStore } from '@/shapes/stores/refreshShapeStore';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { usePreviewRectangleStore } from 'shared/map/stores/previewRectangleStore';
import { usePreviewPolygonStore } from 'shared/map/stores/previewPolygonStore';

export interface CreateShape {
  coordinate: Coordinate;
  width?: number;
  length?: number;
  sideArea?: number;
  name?: string;
  storageArea?: CreateShapeStorageArea;
}

export interface CreateShapeStorageArea {
  type: StorageAreaType;
  allowedTypes: StorageAreaType[];
}

export interface EditShape {
  shapeId: string;
  multiple: boolean;
}

export interface CopyShape {
  shapeId: string;
}

const logSorterTypes = new Set([StorageAreaType.LogSorterBin]);

export const useSelectedShapeStore = defineStore('selectedShape', () => {
  const rectangleStore = usePreviewRectangleStore();
  const polygonStore = usePreviewPolygonStore();
  const storageAreaStore = useStorageAreaStore();
  const shapeStore = useShapeStore();
  const refreshShapeStore = useRefreshShapeStore();
  const refreshStorageAreaStore = useRefreshStorageAreaStore();
  const selectedStorageAreaStore = useSelectedStorageAreaStore();

  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  // TODO: Hantera att det är flera storage area samtidigt...
  const { name: storageAreaName, type: storageAreaType } = storeToRefs(selectedStorageAreaStore);

  const mode = ref<ShapeMode | undefined>(ShapeMode.Rectangle);
  const activeTab = ref<'storageArea' | 'shape'>('storageArea');

  const hasStorageArea = computed(() => selectedStorageAreaStore.operation !== 'none');
  const hasLogSorter = computed(() => logSorterTypes.has(storageAreaType.value));

  const geoJson = computed<ShapeGeoJson | undefined>(() => {
    if (mode.value === ShapeMode.Polygon) {
      return polygonStore.geoJson;
    }
    return rectangleStore.geoJson;
  });

  const editMultiple = computed(() => selectedShapeIds.value.length > 1);

  const allUpdatedShapes = computed<ShapeGeoJson[]>(() => {
    let result: ShapeGeoJson[] = [];

    result.push(...rectangleStore.allGeoJsonShapes);
    result.push(...polygonStore.allGeoJsonShapes);

    return result;
  });

  const color = ref<string | null>(null);
  const originalColor = ref<string | null>(null);
  const colorChanged = computed<boolean>(() => {
    return color.value !== originalColor.value;
  });

  const operation = ref<'create' | 'edit' | 'none'>('none');
  const showUI = ref<boolean>();
  const selectedShapeIds = ref<string[]>([]);
  const currentShape = ref<ShapeResponse | undefined>();
  const lastKnownCoordinate = ref<Coordinate | undefined>();

  const colorEnabled = computed<boolean>(() => {
    if (selectedShapeIds.value.length <= 1) {
      return true;
    }

    const shapes = selectedShapeIds.value
      .map((id) => shapeStore.getById(id))
      .filter((s) => s !== undefined);
    if (shapes.length === 0) {
      return false;
    }

    const firstColor = shapes[0]!.color;
    return shapes.every((shape) => shape!.color === firstColor);
  });

  const shapeGeoJson = ref<GeoJSON.Feature>();
  const selectionEnabled = ref<boolean>(true);

  const name = computed(() => {
    if (storageAreaName.value.length > 0) {
      return storageAreaName.value;
    }

    if (selectedShapeIds.value.length > 1) {
      return translations.value.shapes.multipleNames(selectedShapeIds.value.length);
    } else if (selectedShapeIds.value.length > 0) {
      return translations.value.shapes.name(selectedShapeIds.value[0]! ?? '');
    }

    if (operation.value === 'create') {
      if (hasStorageArea.value) {
        return translations.value.storageAreas.actions.create.title;
      } else {
        return translations.value.shapes.actions.create.title;
      }
    }

    return '';
  });

  const shapeChanged = computed(() => {
    if (operation.value === 'create') return true;
    if (operation.value === 'none') return false;

    if (colorChanged.value) return true;

    if (mode.value === ShapeMode.Polygon) {
      return polygonStore.changed;
    }

    if (mode.value === ShapeMode.Rectangle) {
      return rectangleStore.changed;
    }

    if (editMultiple.value) {
      return rectangleStore.changed || polygonStore.changed;
    }
  });

  watch<ShapeMode | undefined>(mode, (newMode, oldMode) => {
    console.log('Mode changed from', oldMode, 'to', newMode);

    if (newMode === undefined) {
      return;
    }

    console.log('Current operation:', operation.value);
    if (operation.value === 'create') {
      if (oldMode === ShapeMode.Rectangle && rectangleStore.coordinate) {
        lastKnownCoordinate.value = rectangleStore.coordinate;
      } else if (oldMode === ShapeMode.Polygon && polygonStore.centerPoint) {
        lastKnownCoordinate.value = {
          longitude: polygonStore.centerPoint[0]!,
          latitude: polygonStore.centerPoint[1]!,
        };
      }

      if (oldMode === ShapeMode.Polygon) {
        polygonStore.stop();
      } else if (oldMode === ShapeMode.Rectangle) {
        rectangleStore.stop();
      }

      const mode: ShapeMode = newMode as ShapeMode;
      switch (mode) {
        case ShapeMode.Rectangle:
          if (lastKnownCoordinate.value) {
            rectangleStore.create({
              coordinate: lastKnownCoordinate.value,
            });
          }
          break;
        case ShapeMode.Polygon:
          polygonStore.create();
          break;
      }
    } else {
      switch (oldMode) {
        case ShapeMode.Polygon:
          polygonStore.stop();
          break;
        case ShapeMode.Rectangle:
          rectangleStore.stop();
          break;
      }
    }
  });

  shapeStore.changed.on((ev) => {
    for (const shapeId of selectedShapeIds.value) {
      if (shapeId === ev.id) {
        if (ev.action === 'removed') {
          deselect(ev.id);
        }
      }
    }
  });

  function create(action: CreateShape) {
    operation.value = 'create';
    currentShape.value = undefined;
    selectedShapeIds.value = [];
    shapeGeoJson.value = undefined;
    color.value = null;
    originalColor.value = null;
    mode.value = ShapeMode.Rectangle;
    lastKnownCoordinate.value = action.coordinate;

    rectangleStore.create({
      coordinate: action.coordinate,
      width: action.width,
      length: action.length,
    });

    if (action.storageArea) {
      selectedStorageAreaStore.create({
        type: action.storageArea.type,
        allowedTypes: action.storageArea.allowedTypes,
        name: action.name ?? '',
      });
      activeTab.value = 'storageArea';
    } else {
      selectedStorageAreaStore.hide();
      activeTab.value = 'shape';
    }

    showUI.value = true;
  }

  async function edit(action: EditShape) {
    shapeGeoJson.value = undefined;

    const oldOperation = operation.value;

    operation.value = 'edit';

    await refreshShapeStore.initialize();

    const orignalShape = shapeStore.getById(action.shapeId);
    Assert.definied(orignalShape);
    const shape = JSON.parse(JSON.stringify(orignalShape));
    const feature = shape.shapeGeoJson;
    Assert.definied(feature);

    currentShape.value = shape;

    const storageAreaId = shape.storageAreaId;

    if (action.multiple) {
      const hasCurrentStorageArea =
        selectedShapeIds.value.length > 0
          ? shapeStore.getById(selectedShapeIds.value[0]!)?.storageAreaId !== null
          : storageAreaId !== null;

      const newShapeHasStorageArea = storageAreaId !== null;

      // Check if shape mode matches (rectangle vs polygon)
      const newShapeMode = getMode(feature);
      const currentShapeMode =
        selectedShapeIds.value.length > 0 && selectedShapeIds.value[0]
          ? (() => {
              const firstShape = shapeStore.getById(selectedShapeIds.value[0]);
              return firstShape?.shapeGeoJson ? getMode(firstShape.shapeGeoJson) : newShapeMode;
            })()
          : newShapeMode;

      // Only allow selection if storage area status matches AND shape mode matches
      if (hasCurrentStorageArea === newShapeHasStorageArea && currentShapeMode === newShapeMode) {
        selectedShapeIds.value = [...selectedShapeIds.value, shape.id];

        // Only set mode to undefined if there are actually multiple shapes
        if (selectedShapeIds.value.length > 1) {
          mode.value = undefined;
        } else {
          mode.value = getMode(feature);
        }
      } else {
        return;
      }
    } else {
      polygonStore.stop();
      rectangleStore.stop();

      selectedShapeIds.value = [shape.id];
      mode.value = getMode(feature);
    }

    if (selectedShapeIds.value.length > 1) {
      const allShapes = selectedShapeIds.value
        .map((id) => shapeStore.getById(id))
        .filter((s) => s !== undefined);
      const firstColor = allShapes[0]!.color;
      const allSameColor = allShapes.every((s) => s!.color === firstColor);

      if (allSameColor) {
        color.value = firstColor;
        originalColor.value = firstColor;
      } else {
        color.value = null;
        originalColor.value = null;
      }
    } else {
      color.value = shape.color;
      originalColor.value = shape.color;
    }

    if (storageAreaId !== null) {
      await refreshStorageAreaStore.initialize();

      const storageArea = storageAreaStore.getById(storageAreaId);

      if (storageArea) {
        selectedStorageAreaStore.edit({
          storageArea: storageArea,
          multiple: action.multiple,
        });
      } else {
        selectedStorageAreaStore.hide();
      }

      if (oldOperation === 'none') {
        activeTab.value = 'storageArea';
      }
    } else {
      selectedStorageAreaStore.hide();

      if (activeTab.value === 'storageArea') {
        activeTab.value = 'shape';
      }
    }

    showUI.value = true;
    shapeGeoJson.value = shape.shapeGeoJson ?? undefined;

    switch (getMode(feature)) {
      case ShapeMode.Rectangle:
        rectangleStore.edit({
          feature: feature,
          shapeId: shape.id,
          addToSelection: action.multiple,
        });
        break;
      case ShapeMode.Polygon:
        polygonStore.edit({
          feature: feature,
          shapeId: shape.id,
          addToSelection: action.multiple,
        });
        break;
    }
  }

  async function copy(action: CopyShape) {
    shapeGeoJson.value = undefined;

    const oldOperation = operation.value;

    operation.value = 'create';
    currentShape.value = undefined;
    selectedShapeIds.value = [];
    mode.value = ShapeMode.Rectangle;

    await refreshShapeStore.initialize();
    const orignalShape = shapeStore.getById(action.shapeId);
    Assert.definied(orignalShape);
    const shape = JSON.parse(JSON.stringify(orignalShape));

    currentShape.value = shape;
    color.value = shape.color;
    originalColor.value = shape.color;
    const feature = shape.shapeGeoJson;

    Assert.definied(feature);

    mode.value = getMode(feature);

    const storageAreaId = shape.storageAreaId;

    if (storageAreaId !== null) {
      await refreshStorageAreaStore.initialize();

      const storageArea = storageAreaStore.getById(storageAreaId);

      if (storageArea) {
        selectedStorageAreaStore.copy({
          storageArea,
        });
      } else {
        selectedStorageAreaStore.hide();
      }

      if (oldOperation === 'none') {
        activeTab.value = 'storageArea';
      }
    } else {
      selectedStorageAreaStore.hide();

      if (activeTab.value === 'storageArea') {
        activeTab.value = 'shape';
      }
    }

    shapeGeoJson.value = shape.shapeGeoJson ?? undefined;

    switch (mode.value) {
      case ShapeMode.Rectangle:
        rectangleStore.copy({
          feature: feature,
        });
        break;
      case ShapeMode.Polygon:
        polygonStore.copy(feature);
        break;
    }

    showUI.value = true;
  }

  function deselect(shapeId: string | null, keepUIOpen?: boolean) {
    if (shapeId) {
      selectedShapeIds.value = selectedShapeIds.value.filter((id) => id !== shapeId);
      rectangleStore.remove(shapeId);
      polygonStore.remove(shapeId);

      // Switch to single edit mode if only one shape remains
      if (selectedShapeIds.value.length === 1) {
        const remainingShapeId = selectedShapeIds.value[0]!;
        const remainingShape = shapeStore.getById(remainingShapeId);

        if (remainingShape) {
          const feature = remainingShape.shapeGeoJson;
          if (feature) {
            mode.value = getMode(feature);
            shapeGeoJson.value = feature;
            color.value = remainingShape.color;
            originalColor.value = remainingShape.color;
          }
        }
      }
    } else {
      selectedShapeIds.value = [];
    }

    if (selectedShapeIds.value.length === 0) {
      operation.value = 'none';
      rectangleStore.stop();
      polygonStore.stop();
      selectedStorageAreaStore.hide();
      showUI.value = keepUIOpen ?? false;
      shapeGeoJson.value = undefined;
      currentShape.value = undefined;
      color.value = null;
      originalColor.value = null;
      lastKnownCoordinate.value = undefined;
    }
  }

  function modeChanged() {
    if (shapeGeoJson.value) {
      switch (mode.value) {
        case ShapeMode.Polygon:
          polygonStore.edit({
            feature: shapeGeoJson.value,
            shapeId: currentShape.value?.id!,
            addToSelection: false,
          });
          break;
        case ShapeMode.Rectangle:
          rectangleStore.edit({
            feature: shapeGeoJson.value,
            shapeId: currentShape.value?.id!,
            addToSelection: false,
          });
          break;
      }
    } else {
      switch (mode.value) {
        case ShapeMode.Polygon:
          polygonStore.create();
          break;
        case ShapeMode.Rectangle:
          break;
      }
    }
  }

  return {
    create,
    edit,
    deselect,
    modeChanged,
    copy,
    activeTab,
    hasStorageArea,
    showUI,
    operation,
    selectedShapeIds,
    name,
    shapeChanged,
    mode,
    geoJson,
    allUpdatedShapes,
    selectionEnabled,
    hasLogSorter,
    currentShape,
    color,
    colorChanged,
    colorEnabled,
    editMultiple,
  };
});
